import { useState, useEffect, useContext, ReactNode } from "react";
import { TextField } from "@fluentui/react";
import { Button, Tooltip } from "@fluentui/react-components";
import { Send24Filled } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import styles from "./QuestionInput.module.css";
import { SpeechInput } from "./SpeechInput";
import { LoginContext } from "../../loginContext";
import { requireLogin } from "../../authConfig";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
    /** Optional content rendered in the toolbar before the mic (e.g. an attach-file affordance). */
    attachmentSlot?: ReactNode;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, initQuestion, attachmentSlot }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const { loggedIn } = useContext(LoginContext);
    const { t } = useTranslation();
    const [isComposing, setIsComposing] = useState(false);

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (isComposing) return;

        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const handleCompositionStart = () => {
        setIsComposing(true);
    };
    const handleCompositionEnd = () => {
        setIsComposing(false);
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (!newValue) {
            setQuestion("");
        } else {
            setQuestion(newValue);
        }
    };

    const disableRequiredAccessControl = requireLogin && !loggedIn;
    const sendQuestionDisabled = disabled || !question.trim() || disableRequiredAccessControl;

    if (disableRequiredAccessControl) {
        placeholder = "Please login to continue...";
    }

    return (
        <div className={styles.questionInputContainer}>
            <TextField
                className={styles.questionInputTextArea}
                disabled={disableRequiredAccessControl}
                placeholder={placeholder}
                multiline
                resizable={false}
                autoAdjustHeight
                borderless
                value={question}
                onChange={onQuestionChange}
                onKeyDown={onEnterPress}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
            />
            {/* Teams-style toolbar: attach + mic on the left, separator, send on the right. */}
            <div className={styles.questionInputToolbar}>
                <div className={styles.questionInputToolbarLeft}>
                    {attachmentSlot}
                    <SpeechInput updateQuestion={setQuestion} />
                </div>
                <div className={styles.questionInputToolbarRight}>
                    <div className={styles.questionInputToolbarSeparator} />
                    <Tooltip content={t("tooltips.submitQuestion")} relationship="label">
                        <Button
                            appearance="subtle"
                            shape="circular"
                            icon={<Send24Filled />}
                            disabled={sendQuestionDisabled}
                            onClick={sendQuestion}
                            aria-label={t("tooltips.submitQuestion")}
                            className={styles.questionInputSendButton}
                        />
                    </Tooltip>
                </div>
            </div>
            <div className={styles.questionInputBottomBorder} />
        </div>
    );
};
